import {
  AttachMoney,
  Inventory,
  Payment,
  People
} from '@mui/icons-material';
import { Box, CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import styled from 'styled-components';
import apiClient from '../api/client';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const PageTitle = styled.h1`
  font-family: 'Inter', sans-serif;
  color: white;
  font-size: 2rem;
  margin-bottom: 2rem;
  font-weight: 600;
`;

const StyledCard = styled.div`
  padding: 24px;
  min-height: ${props => props.isChart ? '300px' : 'auto'};
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
  }
  
  @media (min-width: 960px) {
    min-height: ${props => props.isChart ? '400px' : 'auto'};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 15px;
  align-items: center;
  margin-bottom: 16px;
`;

const CardTitle = styled.h6`
  font-family: 'Inter', sans-serif;
  color: white;
  font-weight: 500;
  font-size: 1rem;
  margin: 0;
  opacity: 0.9;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${props => `${props.color}22`};
`;

const CardValue = styled.h4`
  font-family: 'Inter', sans-serif;
  color: white;
  font-size: 1.75rem;
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const CardSecondary = styled.p`
  font-family: 'Inter', sans-serif;
  color: rgba(255,255,255,0.7);
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
`;

const StatCard = ({ title, value, icon, color, secondaryText }) => (
  <StyledCard>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      <IconWrapper color={color}>
        {icon}
      </IconWrapper>
    </CardHeader>
    <div>
      <CardValue>
        {typeof value === 'number' ? new Intl.NumberFormat('ru-RU', {
          style: 'decimal',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value) : value}
      </CardValue>
      {secondaryText && (
        <CardSecondary>{secondaryText}</CardSecondary>
      )}
    </div>
  </StyledCard>
);

const ChartCard = styled(StyledCard)`
  padding: 24px;
  min-height: 400px;
  margin-top: 2rem;
`;

const ChartTitle = styled(CardTitle)`
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
`;

const CustomTooltip = styled.div`
  background: rgba(0, 0, 0, 0.8);
  border: none;
  border-radius: 8px;
  padding: 12px;
  color: white;
  font-family: 'Inter', sans-serif;

  .label {
    margin-bottom: 8px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
  }

  .value {
    font-size: 1rem;
    font-weight: 500;
  }
`;

const DashboardPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalDeals: 0,
    totalIncome: 0,
    totalProducts: 0,
    totalClients: 0,
    totalCategories: 0,
    totalExpenses: 0,
    completedExpenses: 0,
    pendingExpenses: 0,
    monthlyStats: [],
    categoryStats: [],
    productStats: [],
    clientStats: []
  });

  const [cmsHero, setCmsHero] = useState(null);

  useEffect(() => {
    fetchStats();
  }, [user]);

  useEffect(() => {
    // Тянем блоки с CMS страницы "home" через публичный роут с public_key (если есть)
    const loadCms = async () => {
      try {
        const publicKey = localStorage.getItem('org_public_key');
        if (!publicKey) return;
        const base = apiClient.defaults?.baseURL || '';
        const res = await fetch(`${base}/public/cms/pages/home?public_key=${encodeURIComponent(publicKey)}`);
        if (res.ok) {
          const page = await res.json();
          setCmsHero(page);
        }
      } catch (_) { }
    };
    loadCms();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [dealsRes, productsRes, clientsRes, categoriesRes, expensesRes] = await Promise.all([
        apiClient.get('/deals'),
        apiClient.get('/products'),
        apiClient.get('/clients'),
        apiClient.get('/categories'),
        apiClient.get('/expenses')
      ]);

      console.log('Полученные сделки:', dealsRes.data);

      const totalIncome = dealsRes.data.reduce((sum, deal) => {
        const amount = parseFloat(deal.amount);
        console.log('Обработка сделки:', { id: deal.id, amount, type: typeof amount });
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);

      console.log('Рассчитанный общий доход:', totalIncome);

      const products = productsRes.data;
      const clients = clientsRes.data;
      const categories = categoriesRes.data;
      const expenses = expensesRes.data;

      // Статистика по месяцам
      const monthlyStats = Array.from({ length: 12 }, (_, i) => {
        const month = new Date(2024, i, 1).toLocaleString('ru', { month: 'short' });
        const monthDeals = dealsRes.data.filter(deal => new Date(deal.created_at).getMonth() === i);
        const income = monthDeals.reduce((sum, deal) => sum + deal.amount, 0);
        const count = monthDeals.length;
        return { month, income, count };
      });

      // Статистика по категориям
      const categoryStats = categories.map(category => {
        const categoryProducts = products.filter(p => p.category_id === category.id);
        const categoryDeals = dealsRes.data.filter(d =>
          categoryProducts.some(p => p.id === d.product_id)
        );
        return {
          name: category.name,
          products: categoryProducts.length,
          deals: categoryDeals.length,
          income: categoryDeals.reduce((sum, deal) => sum + deal.amount, 0)
        };
      });

      // Топ продуктов
      const productStats = products.map(product => {
        const productDeals = dealsRes.data.filter(d => d.product_id === product.id);
        return {
          name: product.name,
          deals: productDeals.length,
          income: productDeals.reduce((sum, deal) => sum + deal.amount, 0)
        };
      }).sort((a, b) => b.income - a.income).slice(0, 5);

      // Статистика по клиентам
      const clientStats = clients.map(client => {
        const clientDeals = dealsRes.data.filter(d => d.client_id === client.id);
        return {
          name: client.name,
          deals: clientDeals.length,
          income: clientDeals.reduce((sum, deal) => sum + deal.amount, 0)
        };
      }).sort((a, b) => b.income - a.income).slice(0, 5);

      setStats({
        totalIncome,
        totalDeals: dealsRes.data.length,
        totalProducts: productsRes.data.length,
        totalClients: clientsRes.data.length,
        totalCategories: categoriesRes.data.length,
        totalExpenses: expenses.length,
        completedExpenses: expenses.filter(e => e.status === 'completed').length,
        pendingExpenses: expenses.filter(e => e.status === 'pending').length,
        monthlyStats,
        categoryStats,
        productStats,
        clientStats,
        deals: dealsRes.data,
        products: productsRes.data,
        clients: clientsRes.data,
        categories: categoriesRes.data,
        expenses
      });

      setError(null);
    } catch (err) {
      console.error('Ошибка при загрузке статистики:', err);
      setError('Не удалось загрузить данные статистики');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <PageTitle>Ошибка</PageTitle>
          <CardSecondary>{error}</CardSecondary>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Герой из CMS (если есть) */}
        {cmsHero?.components?.length ? (
          <Box sx={{ mb: 3 }}>
            {cmsHero.components.map((c, i) => {
              if (c.type === 'text') return (
                <Typography key={i} variant="h5" sx={{ color: 'white', mb: 1 }}>{c.props?.content}</Typography>
              );
              if (c.type === 'image') return (
                <img key={i} alt={c.props?.alt || ''} src={c.props?.src || ''} style={{ maxWidth: '100%', borderRadius: 12, marginBottom: 16 }} />
              );
              if (c.type === 'button') return (
                <a key={i} href={c.props?.url || '#'} style={{ display: 'inline-block', padding: '8px 14px', background: '#1976d2', color: '#fff', borderRadius: 8, textDecoration: 'none', marginRight: 8 }}>
                  {c.props?.label || 'Подробнее'}
                </a>
              );
              return null;
            })}
          </Box>
        ) : (
          <PageTitle>
            Общая статистика
          </PageTitle>
        )}

        <Grid container spacing={3}>
          {/* Основные показатели */}
          <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Общий доход"
              value={`${new Intl.NumberFormat('ru-RU', {
                style: 'decimal',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(stats.totalIncome)} ₸`}
              icon={<AttachMoney sx={{ color: '#0088FE' }} />}
              color="#0088FE"
              secondaryText={`${stats.totalDeals} сделок`}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Клиенты"
              value={stats.totalClients}
              icon={<People sx={{ color: '#00C49F' }} />}
              color="#00C49F"
              secondaryText="Активных клиентов"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Товары"
              value={stats.totalProducts}
              icon={<Inventory sx={{ color: '#FFBB28' }} />}
              color="#FFBB28"
              secondaryText={`${stats.totalCategories} категорий`}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <StatCard
              title="Расходы"
              value={stats.completedExpenses}
              icon={<Payment sx={{ color: '#FF8042' }} />}
              color="#FF8042"
              secondaryText={`${stats.pendingExpenses} в ожидании`}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ChartCard>
              <ChartTitle>Динамика доходов</ChartTitle>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart
                  data={stats.monthlyStats}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0088FE" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0088FE" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.1)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    stroke="rgba(255,255,255,0.5)"
                    tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 12, fontFamily: 'Inter' }}
                  />
                  <YAxis
                    stroke="rgba(255,255,255,0.5)"
                    tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 12, fontFamily: 'Inter' }}
                    tickFormatter={(value) => new Intl.NumberFormat('ru-RU', {
                      notation: 'compact',
                      compactDisplay: 'short'
                    }).format(value)}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <CustomTooltip>
                            <div className="label">{label}</div>
                            <div className="value">
                              {new Intl.NumberFormat('ru-RU', {
                                style: 'decimal',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                              }).format(payload[0].value)} ₸
                            </div>
                          </CustomTooltip>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="income"
                    stroke="#0088FE"
                    strokeWidth={2}
                    fill="url(#incomeGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};

export default DashboardPage; 